import type {AiChatProTipType} from '../../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-type';
import type {AiChatLandingTipType} from '@Components/ai-chat-landing-hero/ai-chat-landing-tip-type';

const LOCAL_STORAGE_KEY = 'pmw_ai_chat_seen_pro_tips';
const LOCAL_STORAGE_RESET_VERSION_KEY = 'pmw_ai_chat_seen_pro_tips_reset_version';

export function getSeenTipsFromLocalStorage(): Set<string> {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      return new Set(parsed);
    }
  } catch (e) {
    console.error(e);
  }
  return new Set();
}

export function saveSeenTipsToLocalStorage(seen: Set<string>): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(seen)));
  } catch (e) {
    console.error(e);
  }
}

export function getStoredResetVersion(): number | null {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_RESET_VERSION_KEY);
    if (stored === null) {
      return null;
    }
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function setStoredResetVersion(version: number): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_RESET_VERSION_KEY, String(version));
  } catch (e) {
    console.error(e);
  }
}

export interface SeenTipsBackendResponse {
  tips: string[];
  hasAiChatHistory: boolean;
  resetVersion: number | null;
}

interface SeenTipsApiResponse {
  status: string;
  data?: {
    seenTips: string[];
    hasAiChatHistory: boolean;
    resetVersion?: number;
  };
}

export function fetchSeenTipsFromBackend(): Promise<SeenTipsBackendResponse> {
  return fetch(window.PMW.util.site_url('ai/getSeenProTips'))
    .then((res: Response) => res.json() as Promise<SeenTipsApiResponse>)
    .then((data: SeenTipsApiResponse) => {
      if (data.status === 'success' && data.data) {
        return {
          tips: Array.isArray(data.data.seenTips) ? data.data.seenTips : [],
          hasAiChatHistory: data.data.hasAiChatHistory,
          resetVersion: typeof data.data.resetVersion === 'number' ? data.data.resetVersion : null,
        };
      }
      return {tips: [], hasAiChatHistory: false, resetVersion: null};
    })
    .catch(() => ({tips: [], hasAiChatHistory: false, resetVersion: null}));
}

const RECENTLY_SHOWN_LANDING_TIP_KEY = 'pmw_ai_chat_recently_shown_landing_tip';
const RECENTLY_SHOWN_TTL_MS = 30 * 60 * 1000;

interface RecentlyShownTip {
  type: string;
  timestamp: number;
}

export function getRecentlyShownLandingTip(): string | null {
  try {
    const stored = localStorage.getItem(RECENTLY_SHOWN_LANDING_TIP_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored) as RecentlyShownTip;
    if (Date.now() - parsed.timestamp > RECENTLY_SHOWN_TTL_MS) {
      localStorage.removeItem(RECENTLY_SHOWN_LANDING_TIP_KEY);
      return null;
    }
    return parsed.type;
  } catch {
    return null;
  }
}

export function setRecentlyShownLandingTip(tipType: string): void {
  try {
    const data: RecentlyShownTip = {type: tipType, timestamp: Date.now()};
    localStorage.setItem(RECENTLY_SHOWN_LANDING_TIP_KEY, JSON.stringify(data));
  } catch (e) {
    console.error(e);
  }
}

export function markTipSeenInLocalStorage(tipType: string): void {
  const seen = getSeenTipsFromLocalStorage();
  seen.add(tipType);
  saveSeenTipsToLocalStorage(seen);
}

export function markTipSeenOnBackend(tipType: AiChatProTipType | AiChatLandingTipType): void {
  try {
    void fetch(window.PMW.util.site_url('ai/markProTipSeen'), {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: `tipType=${encodeURIComponent(tipType)}`,
    });
  } catch (e) {
    console.error(e);
  }
}
